import { strapiClient } from "@shared/api";
import rebuild from "@shared/api/rebuild";
import { NotFoundError } from "@shared/utils/not-found-error";
import { getSeoConfig } from "@shared/utils/seo/seo-helper";

import { type ArticleType, mapDtoToArticlePage } from "@/_pages/article/model";

import { isNotFound } from "./is-not-found";

type BlogPostResponse = {
  article: ArticleType;
};

/**
 * Статья блога собирается по шаблону «Страниц» (как /travelguide): те же
 * блоки контента, включая форму документа
 */
export const getBlogPost = async (slug: string): Promise<BlogPostResponse> => {
  const postSearchParams = {
    filters: {
      slug: {
        $eq: slug,
      },
    },
    populate: {
      hero: {
        populate: {
          author: { populate: "*" },
          mainImage: { populate: "*" },
        },
      },
      content: {
        on: {
          "article.section-name": {
            populate: "*",
          },
          "article.text-block": {
            populate: "*",
          },
          "article.quote-with-author": {
            populate: "*",
          },
          "article.table": {
            populate: {
              tabletRows: {
                populate: "*",
              },
              columnTitle: {
                populate: "*",
              },
              columnSubtitle: {
                populate: "*",
              },
            },
          },
          "article.gallery": {
            populate: "*",
          },
          "article.video": {
            populate: "*",
          },
          "ui.document-form": {
            populate: {
              document: {
                populate: "*",
              },
              button: {
                populate: "*",
              },
            },
          },
        },
      },
      seo: {
        populate: {
          ogImage: {
            populate: "*",
          },
        },
      },
    },
  };

  const posts = strapiClient.collection("blog-posts");

  const response = await posts.find({ ...postSearchParams }).catch((error) => {
    // Коллекции ещё нет в Strapi (бэкенд не выкачен) — это 404, а не 500
    if (isNotFound(error)) {
      throw new NotFoundError(`Blog post with slug "${slug}" not found`);
    }

    throw error;
  });

  const currentPost = mapDtoToArticlePage(response)[0];

  if (!currentPost) {
    throw new NotFoundError(`Blog post with slug "${slug}" not found`);
  }

  return rebuild({
    article: {
      ...currentPost,
      seo: getSeoConfig.article(currentPost),
    },
  }) as BlogPostResponse;
};
